import { computed, inject, Injectable } from '@angular/core';
import { ILocation, IRegion } from '@core/interfaces';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class LocationsService {
  private readonly _authService = inject(AuthService);
  private readonly _localStorageService = inject(LocalStorageService);
  private readonly _selectedRegions$ = new BehaviorSubject<IRegion[]>([]);
  private readonly _selectedLocations$ = new BehaviorSubject<ILocation[]>([]);
  userRegions = computed(() => this._authService.currentUser()?.regions ?? []);
  userLocations = computed(
    () => this._authService.currentUser()?.locations ?? [],
  );

  constructor() {
    this.initUserRegions();
    this.initUserLocations();
  }

  get selectedRegions$() {
    return this._selectedRegions$.asObservable();
  }

  get selectedLocations$() {
    return this._selectedLocations$.asObservable();
  }

  get selectedRegionsIds$() {
    return this.selectedRegions$.pipe(
      map(regions => regions.map(region => region.id)),
    );
  }

  get selectedLocationsIds$() {
    return this.selectedLocations$.pipe(
      map(locations => locations.map(location => location.id)),
    );
  }

  updateSelectedRegions(regions: IRegion[]) {
    this._localStorageService.setItem(
      'selectedRegionsIds',
      regions.map(region => region.id),
    );
  }

  updateSelectedLocations(locations: ILocation[]) {
    this._localStorageService.setItem(
      'selectedLocationIds',
      locations.map(location => location.id),
    );
  }

  private initUserRegions() {
    this._localStorageService
      .watchKeyWithInitialValue<number[]>('selectedRegionsIds')
      .subscribe(regions => {
        const selectedRegions = this.userRegions().filter(region =>
          (regions ?? []).includes(region.id),
        );
        this._selectedRegions$.next(selectedRegions);
      });
  }

  private initUserLocations() {
    this._localStorageService
      .watchKeyWithInitialValue<number[]>('selectedLocationIds')
      .subscribe(locations => {
        const selectedLocations = this.userLocations().filter(location =>
          (locations ?? []).includes(location.id),
        );
        this._selectedLocations$.next(selectedLocations);
      });
  }
}
